import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Database,
  FileText,
  Upload,
  Trash2,
  ChevronRight,
  X,
  File,
  FileSpreadsheet,
  ArrowLeft,
  Download,
  Eye,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  FileCode,
  FileJson,
  RefreshCw,
  Type,
} from 'lucide-react';
import { knowledgeService, KnowledgeBase, KnowledgeDocument } from '../../services/knowledge.service';
import toast from 'react-hot-toast';

export const KnowledgeBasePage: React.FC = () => {
  const { id: kbIdFromUrl } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [newKBName, setNewKBName] = useState('');
  const [newKBDescription, setNewKBDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [viewingDocument, setViewingDocument] = useState<KnowledgeDocument | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadTab, setUploadTab] = useState<'files' | 'text'>('files');
  const [textDocName, setTextDocName] = useState('');
  const [textDocContent, setTextDocContent] = useState('');
  const [isAddingText, setIsAddingText] = useState(false);
  const [textAddedCount, setTextAddedCount] = useState(0);

  const [isLoadingKBs, setIsLoadingKBs] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileStatuses, setUploadFileStatuses] = useState<
    { name: string; size: number; status: 'waiting' | 'uploading' | 'queued' | 'failed'; error?: string }[]
  >([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchKnowledgeBases();
  }, []);

  // When KB list loads and URL has an ID, select that KB
  useEffect(() => {
    if (kbIdFromUrl && knowledgeBases.length > 0 && !selectedKnowledgeBase) {
      const kb = knowledgeBases.find((k) => k.id === kbIdFromUrl);
      if (kb) setSelectedKnowledgeBase(kb);
    }
  }, [kbIdFromUrl, knowledgeBases]);

  useEffect(() => {
    if (selectedKnowledgeBase) {
      fetchDocuments(selectedKnowledgeBase.id);
    }
  }, [selectedKnowledgeBase]);

  // Auto-refresh when documents are processing
  useEffect(() => {
    if (!selectedKnowledgeBase) return;
    const hasProcessing = documents.some(doc =>
      !['ready', 'error', 'awaiting_api_key'].includes(doc.status)
    );
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      fetchDocuments(selectedKnowledgeBase.id);
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedKnowledgeBase, documents]);

  const fetchKnowledgeBases = async () => {
    setIsLoadingKBs(true);
    setError(null);
    try {
      const data = await knowledgeService.getKnowledgeBases();
      setKnowledgeBases(data);
    } catch (err) {
      setError('Failed to load knowledge bases');
      console.error('Error fetching knowledge bases:', err);
    } finally {
      setIsLoadingKBs(false);
    }
  };

  const fetchDocuments = async (kbId: string) => {
    setIsLoadingDocs(true);
    setError(null);
    try {
      const data = await knowledgeService.getDocuments(kbId);
      setDocuments(data);
    } catch (err) {
      setError('Failed to load documents');
      console.error('Error fetching documents:', err);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const getFileIcon = (type: string) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('pdf')) return <FileText className="w-6 h-6 text-red-500" />;
    if (t.includes('word') || t.includes('docx')) return <File className="w-6 h-6 text-blue-600" />;
    if (t.includes('csv') || t.includes('spreadsheet')) return <FileSpreadsheet className="w-6 h-6 text-green-500" />;
    if (t.includes('md') || t.includes('markdown')) return <FileText className="w-6 h-6 text-purple-500" />;
    if (t.includes('html')) return <FileCode className="w-6 h-6 text-orange-500" />;
    if (t.includes('json')) return <FileJson className="w-6 h-6 text-yellow-600" />;
    if (t.includes('xml')) return <FileCode className="w-6 h-6 text-teal-500" />;
    if (t.includes('text') || t.includes('txt')) return <FileText className="w-6 h-6 text-gray-500" />;
    return <FileText className="w-6 h-6 text-gray-400" />;
  };

  const getStageProgress = (doc: KnowledgeDocument) => {
    const meta = doc.metadata || {};
    const chunks = doc.chunk_count || 0;
    const fileSize = doc.file_size || 0;
    const status = doc.status;

    const stageOrder = ['extracting', 'extracted', 'graphing', 'graphed', 'embedding', 'embedded', 'ready'];
    const stageIdx = stageOrder.indexOf(status);

    // Extracting
    let extractPct = 0;
    if (stageIdx > 0) extractPct = 100;
    else if (status === 'extracting') {
      const offset = Number(meta.extract_offset) || 0;
      extractPct = fileSize > 0 ? Math.round((offset / fileSize) * 100) : 5;
    }

    // Categorizing (graphing)
    let graphPct = 0;
    if (stageIdx > 2) graphPct = 100;
    else if (status === 'graphing') {
      const graphOff = Number(meta.graph_batch_offset) || 0;
      graphPct = chunks > 0 ? Math.round((graphOff / chunks) * 100) : 5;
    }

    // Embedding
    let embedPct = 0;
    if (stageIdx > 4) embedPct = 100;
    else if (status === 'embedding') {
      const embedOff = Number(meta.embed_batch_offset) || 0;
      embedPct = chunks > 0 ? Math.round((embedOff / chunks) * 100) : 5;
    }

    // Finalizing
    const finalizePct = status === 'ready' ? 100 : 0;

    return { extractPct, graphPct, embedPct, finalizePct };
  };

  const getActiveStageInfo = (doc: KnowledgeDocument): { label: string; pct: number } | null => {
    const { extractPct, graphPct, embedPct } = getStageProgress(doc);
    switch (doc.status) {
      case 'pending': return { label: 'Pending', pct: 0 };
      case 'extracting': return { label: 'Extracting', pct: extractPct };
      case 'extracted': return { label: 'Extracting', pct: 100 };
      case 'graphing': return { label: 'Categorizing', pct: graphPct };
      case 'graphed': return { label: 'Categorizing', pct: 100 };
      case 'embedding': return { label: 'Embedding', pct: embedPct };
      case 'embedded': return { label: 'Embedding', pct: 100 };
      case 'ready': return null;
      default: return null;
    }
  };

  const [progressPopoverDocId, setProgressPopoverDocId] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setProgressPopoverDocId(null);
      }
    };
    if (progressPopoverDocId) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [progressPopoverDocId]);

  const openPopover = (docId: string, buttonEl: HTMLElement) => {
    if (progressPopoverDocId === docId) {
      setProgressPopoverDocId(null);
      return;
    }
    const rect = buttonEl.getBoundingClientRect();
    setPopoverPos({ top: rect.bottom + 4, left: rect.left });
    setProgressPopoverDocId(docId);
  };

  const ProgressPopover: React.FC<{ doc: KnowledgeDocument }> = ({ doc }) => {
    const { extractPct, graphPct, embedPct, finalizePct } = getStageProgress(doc);
    const stages = [
      { label: 'Extracting', pct: extractPct },
      { label: 'Categorizing', pct: graphPct },
      { label: 'Embedding', pct: embedPct },
      { label: 'Finalizing', pct: finalizePct },
    ];

    return ReactDOM.createPortal(
      <div
        ref={popoverRef}
        className="fixed z-[9999] bg-white border-2 border-[#1a1a2e] rounded-lg shadow-[5px_5px_0px_#1a1a2e] p-3 w-56"
        style={{ top: popoverPos.top, left: popoverPos.left }}
      >
        <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Processing Stages</p>
        <div className="space-y-2">
          {stages.map((s) => {
            const isComplete = s.pct === 100;
            const isActive = s.pct > 0 && s.pct < 100;
            return (
              <div key={s.label} className="flex items-center gap-2">
                {isComplete ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                ) : isActive ? (
                  <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin flex-shrink-0" />
                ) : (
                  <div className="w-3.5 h-3.5 rounded-full border border-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isActive ? 'text-gray-900 font-medium' : isComplete ? 'text-gray-500' : 'text-gray-400'}`}>
                      {s.label}
                    </span>
                    <span className={`text-xs ${isActive ? 'text-blue-600 font-medium' : isComplete ? 'text-green-600' : 'text-gray-400'}`}>
                      {s.pct}%
                    </span>
                  </div>
                  {isActive && (
                    <div className="w-full h-1 bg-gray-100 rounded-full mt-0.5">
                      <div className="h-1 bg-blue-500 rounded-full transition-all" style={{ width: `${s.pct}%` }} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>,
      document.body
    );
  };

  const getStatusBadge = (doc: KnowledgeDocument) => {
    const activeInfo = getActiveStageInfo(doc);

    switch (doc.status) {
      case 'ready':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
            <CheckCircle2 className="w-3 h-3" /> Ready
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700">
            <AlertCircle className="w-3 h-3" /> Error
          </span>
        );
      case 'awaiting_api_key':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700">
            <Clock className="w-3 h-3" /> Awaiting API Key
          </span>
        );
      default: {
        const label = activeInfo?.label || 'Processing';
        const pct = activeInfo?.pct ?? 0;
        const colorMap: Record<string, { bg: string; text: string }> = {
          Extracting: { bg: 'bg-blue-50', text: 'text-blue-700' },
          Categorizing: { bg: 'bg-indigo-50', text: 'text-indigo-700' },
          Embedding: { bg: 'bg-purple-50', text: 'text-purple-700' },
          Pending: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
        };
        const colors = colorMap[label] || { bg: 'bg-yellow-50', text: 'text-yellow-700' };
        const isOpen = progressPopoverDocId === doc.id;

        return (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openPopover(doc.id, e.currentTarget);
              }}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} hover:opacity-80 transition-opacity cursor-pointer`}
            >
              <Loader2 className="w-3 h-3 animate-spin" /> {label} {pct}%
            </button>
            {isOpen && <ProgressPopover doc={doc} />}
          </div>
        );
      }
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.file_type === typeFilter;
    return matchesSearch && matchesType;
  });

  const handleCreateKnowledgeBase = async () => {
    if (!newKBName.trim()) return;

    try {
      const newKB = await knowledgeService.createKnowledgeBase({
        name: newKBName,
        description: newKBDescription,
      });
      setKnowledgeBases([newKB, ...knowledgeBases]);
      setShowCreateModal(false);
      setNewKBName('');
      setNewKBDescription('');
    } catch (err) {
      console.error('Error creating knowledge base:', err);
      alert('Failed to create knowledge base');
    }
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 5MB limit`);
        return false;
      }
      return true;
    });
    setSelectedFiles(files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 5MB limit`);
        return false;
      }
      return true;
    });
    setSelectedFiles(files);
  };

  const handleUploadDocuments = async () => {
    if (selectedFiles.length === 0 || !selectedKnowledgeBase) return;

    const statuses = selectedFiles.map((f) => ({
      name: f.name,
      size: f.size,
      status: 'waiting' as const,
    }));
    setUploadFileStatuses(statuses);
    setIsUploading(true);

    for (let i = 0; i < selectedFiles.length; i++) {
      setUploadFileStatuses((prev) =>
        prev.map((item, idx) => (idx === i ? { ...item, status: 'uploading' } : item))
      );
      try {
        await knowledgeService.uploadDocuments(selectedKnowledgeBase.id, [selectedFiles[i]]);
        setUploadFileStatuses((prev) =>
          prev.map((item, idx) => (idx === i ? { ...item, status: 'queued' } : item))
        );
      } catch {
        setUploadFileStatuses((prev) =>
          prev.map((item, idx) => (idx === i ? { ...item, status: 'failed', error: 'Upload failed' } : item))
        );
      }
    }

    setIsUploading(false);
    setSelectedFiles([]);
    await fetchDocuments(selectedKnowledgeBase.id);
  };

  const handleAddText = async () => {
    if (!textDocContent.trim() || !selectedKnowledgeBase) return;
    const name = textDocName.trim() || `Text Entry ${textAddedCount + 1}`;

    setIsAddingText(true);
    try {
      await knowledgeService.addDocument(selectedKnowledgeBase.id, {
        name,
        content: textDocContent.trim(),
        file_type: 'text',
      });
      setTextDocName('');
      setTextDocContent('');
      setTextAddedCount((c) => c + 1);
      await fetchDocuments(selectedKnowledgeBase.id);
    } catch (err) {
      console.error('Error adding text:', err);
      alert('Failed to add text entry. Please try again.');
    } finally {
      setIsAddingText(false);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!selectedKnowledgeBase) return;
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await knowledgeService.deleteDocument(selectedKnowledgeBase.id, docId);
      setDocuments(documents.filter((doc) => doc.id !== docId));
    } catch (err) {
      console.error('Error deleting document:', err);
      alert('Failed to delete document');
    }
  };

  const handleReprocessDocument = async (docId: string) => {
    if (!selectedKnowledgeBase) return;

    try {
      await knowledgeService.reprocessDocument(selectedKnowledgeBase.id, docId);
      setDocuments(documents.map((doc) =>
        doc.id === docId ? { ...doc, status: 'pending' } : doc
      ));
    } catch (err) {
      console.error('Error reprocessing document:', err);
      alert('Failed to reprocess document');
    }
  };

  const handleDeleteKnowledgeBase = async (id: string) => {
    if (!confirm('Are you sure you want to delete this knowledge base? All documents will be removed.')) return;

    try {
      await knowledgeService.deleteKnowledgeBase(id);
      setKnowledgeBases(knowledgeBases.filter((kb) => kb.id !== id));
    } catch (err) {
      console.error('Error deleting knowledge base:', err);
      alert('Failed to delete knowledge base');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!selectedKnowledgeBase) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Knowledge Base</h1>
            <p className="text-gray-500 mt-1">
              Manage document collections for your AI agents
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
          >
            <Plus className="w-4 h-4" />
            Create Knowledge Base
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isLoadingKBs ? (
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
        ) : knowledgeBases.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
                <Database className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No knowledge bases yet
              </h3>
              <p className="text-gray-500 mb-6">
                Create your first knowledge base to organize documents for your AI agents
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
              >
                <Plus className="w-4 h-4" />
                Create Knowledge Base
              </button>
            </div>
          </div>
        ) : (
          /* Knowledge Bases Table */
          <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Documents
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Created
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {knowledgeBases.map((kb) => (
                  <tr
                    key={kb.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => { setSelectedKnowledgeBase(kb); navigate(`/knowledge/${kb.id}`); }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-emerald-500 border-2 border-[#1a1a2e] flex items-center justify-center flex-shrink-0">
                          <Database className="w-5 h-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{kb.name}</p>
                          <p className="text-sm text-gray-500 truncate">
                            {kb.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 hidden md:table-cell">
                      <span className="text-sm text-gray-700">
                        {String(kb.document_count ?? kb.metadata?.documentCount ?? 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden lg:table-cell">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <span className="text-sm text-gray-500">
                        {new Date(kb.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => { setSelectedKnowledgeBase(kb); navigate(`/knowledge/${kb.id}`); }}
                          className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-500 hover:text-emerald-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteKnowledgeBase(kb.id)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Knowledge Base Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setShowCreateModal(false)}
            />
            <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  Create Knowledge Base
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newKBName}
                    onChange={(e) => setNewKBName(e.target.value)}
                    placeholder="e.g., Product Documentation"
                    className="w-full h-12 px-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newKBDescription}
                    onChange={(e) => setNewKBDescription(e.target.value)}
                    placeholder="Describe what this knowledge base contains..."
                    rows={4}
                    className="w-full px-4 py-3 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] resize-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKnowledgeBase}
                  disabled={!newKBName.trim()}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 border-2 border-[#1a1a2e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => { setSelectedKnowledgeBase(null); navigate('/knowledge'); }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {selectedKnowledgeBase.name}
            </h1>
            <p className="text-gray-500 mt-1">
              {selectedKnowledgeBase.description}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowUploadModal(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
        >
          <Upload className="w-4 h-4" />
          Upload Documents
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-12 pl-12 pr-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-[#1a1a2e]/20"
          />
        </div>
        <div className="flex gap-3">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-12 px-4 bg-white border-2 border-[#1a1a2e] rounded-xl text-sm text-gray-900"
          >
            <option value="all">All Types</option>
            <option value="pdf">PDF</option>
            <option value="docx">Word</option>
            <option value="txt">Text</option>
            <option value="md">Markdown</option>
            <option value="html">HTML</option>
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
            <option value="xml">XML</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoadingDocs ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
        </div>
      ) : filteredDocuments.length === 0 ? (
        /* Empty State */
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery || typeFilter !== 'all' ? 'No documents found' : 'No documents yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery || typeFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Upload documents to start building your knowledge base'}
            </p>
            {!searchQuery && typeFilter === 'all' && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
              >
                <Upload className="w-4 h-4" />
                Upload Documents
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
        {/* Awaiting API Key Banner */}
        {documents.some(d => d.status === 'awaiting_api_key') && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-orange-800">Gemini API key required</p>
              <p className="text-orange-700 mt-0.5">
                Some documents are waiting for a Gemini API key to be processed.{' '}
                <button onClick={() => navigate('/settings')} className="underline font-medium hover:text-orange-900">
                  Go to Settings
                </button>{' '}
                to add your key, then reprocess the documents.
              </p>
            </div>
          </div>
        )}
        {/* Documents Table */}
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">
                  Document
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 hidden md:table-cell">
                  Type
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 hidden lg:table-cell">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600 hidden sm:table-cell">
                  Uploaded
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDocuments.map((doc) => (
                <tr
                  key={doc.id}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-50 rounded-lg">
                        {getFileIcon(doc.file_type)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{doc.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 hidden md:table-cell">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-50 text-gray-700 uppercase">
                      {doc.file_type || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {getStatusBadge(doc)}
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <span className="text-gray-500">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setViewingDocument(doc)}
                        className="p-2 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="View document"
                      >
                        <Eye className="w-4 h-4 text-gray-500 hover:text-emerald-600" />
                      </button>
                      <button
                        onClick={() => {
                          if (selectedKnowledgeBase) {
                            knowledgeService.downloadDocument(
                              selectedKnowledgeBase.id,
                              doc.id,
                              doc.name || 'document'
                            );
                          }
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Download document"
                      >
                        <Download className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                      </button>
                      {(doc.status === 'error' || doc.status === 'awaiting_api_key') && (
                        <button
                          onClick={() => handleReprocessDocument(doc.id)}
                          className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                          title={doc.status === 'awaiting_api_key' ? 'Process document' : 'Retry processing'}
                        >
                          <RefreshCw className="w-4 h-4 text-orange-500 hover:text-orange-600" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete document"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* View Document Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setViewingDocument(null)}
          />
          <div className="relative bg-white rounded-xl p-6 max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1 min-w-0 mr-4">
                <h2 className="text-xl font-bold text-gray-900 truncate">
                  {viewingDocument.name}
                </h2>
                <div className="flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 uppercase">
                    {viewingDocument.file_type || 'unknown'}
                  </span>
                  {getStatusBadge(viewingDocument)}
                  <span className="text-xs text-gray-500">
                    {new Date(viewingDocument.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setViewingDocument(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            {/* Document content viewer */}
            {viewingDocument.content && !viewingDocument.content.startsWith('[PDF file:') && !viewingDocument.content.startsWith('[DOCX file:') ? (
              <div className="flex-1 overflow-y-auto bg-gray-50 rounded-xl border-2 border-[#1a1a2e] p-4">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words font-mono">
                  {viewingDocument.content}
                </pre>
              </div>
            ) : viewingDocument.file_url && selectedKnowledgeBase ? (
              <div className="flex-1 overflow-hidden rounded-xl border-2 border-[#1a1a2e]">
                <iframe
                  src={knowledgeService.getDocumentDownloadUrl(selectedKnowledgeBase.id, viewingDocument.id)
                    + `?token=${localStorage.getItem('access_token')}`}
                  className="w-full h-full min-h-[400px]"
                  title={viewingDocument.name}
                />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No content preview available</p>
                  {viewingDocument.file_url && selectedKnowledgeBase && (
                    <button
                      onClick={() => knowledgeService.downloadDocument(
                        selectedKnowledgeBase.id, viewingDocument.id, viewingDocument.name || 'document'
                      )}
                      className="inline-flex items-center gap-1 mt-3 text-sm text-emerald-600 hover:underline"
                    >
                      <Download className="w-4 h-4" />
                      Download file
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
              {selectedKnowledgeBase && (
                <button
                  onClick={() => knowledgeService.downloadDocument(
                    selectedKnowledgeBase.id, viewingDocument.id, viewingDocument.name || 'document'
                  )}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              )}
              <button
                onClick={() => setViewingDocument(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Knowledge Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowUploadModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Knowledge</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Tab Switcher */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-5">
              <button
                onClick={() => setUploadTab('files')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  uploadTab === 'files'
                    ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Upload className="w-4 h-4" />
                Upload Files
              </button>
              <button
                onClick={() => setUploadTab('text')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  uploadTab === 'text'
                    ? 'bg-white text-gray-900 shadow-[3px_3px_0px_#1a1a2e]'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Type className="w-4 h-4" />
                Paste Text
              </button>
            </div>

            {uploadTab === 'files' ? (
              <>
                {/* Drag and Drop Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                    isDragging
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-300 hover:border-emerald-600'
                  }`}
                >
                  <Upload
                    className={`w-12 h-12 mx-auto mb-4 ${
                      isDragging ? 'text-emerald-600' : 'text-gray-400'
                    }`}
                  />
                  <p className="text-gray-900 font-medium mb-1">
                    {isDragging ? 'Drop files here' : 'Drop files here or click to browse'}
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports PDF, DOCX, TXT, MD, CSV, HTML, JSON, XML files (max 5MB each)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.docx,.doc,.txt,.md,.csv,.html,.json,.xml"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Per-file upload progress (shown during/after upload) */}
                {uploadFileStatuses.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-900">
                      Upload Progress ({uploadFileStatuses.filter((f) => f.status === 'queued').length}/{uploadFileStatuses.length})
                    </p>
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {uploadFileStatuses.map((item, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            item.status === 'queued'
                              ? 'bg-green-50 border-green-200'
                              : item.status === 'failed'
                                ? 'bg-red-50 border-red-200'
                                : item.status === 'uploading'
                                  ? 'bg-emerald-50 border-emerald-200'
                                  : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-1.5 bg-white rounded-md">
                              {getFileIcon(item.name.split('.').pop() || '')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(item.size)}</p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            {item.status === 'waiting' && (
                              <Clock className="w-4 h-4 text-gray-400" />
                            )}
                            {item.status === 'uploading' && (
                              <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
                            )}
                            {item.status === 'queued' && (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                            {item.status === 'failed' && (
                              <AlertCircle className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Files (before upload starts) */}
                {selectedFiles.length > 0 && uploadFileStatuses.length === 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium text-gray-900">
                      Selected Files ({selectedFiles.length})
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="p-2 bg-white rounded-lg">
                              {getFileIcon(file.name.split('.').pop() || '')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
                            }
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-400" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setSelectedFiles([]);
                      setUploadFileStatuses([]);
                    }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    {uploadFileStatuses.length > 0 && !isUploading ? 'Done' : 'Cancel'}
                  </button>
                  {(uploadFileStatuses.length === 0 || isUploading) && (
                    <button
                      onClick={handleUploadDocuments}
                      disabled={selectedFiles.length === 0 || isUploading}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 border-2 border-[#1a1a2e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>Upload {selectedFiles.length > 0 && `(${selectedFiles.length})`}</>
                      )}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Paste Text Tab */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                    <input
                      type="text"
                      value={textDocName}
                      onChange={(e) => setTextDocName(e.target.value)}
                      placeholder="e.g. Product FAQ, Return Policy, Pricing Details..."
                      className="w-full px-3 py-2.5 bg-white border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none transition-colors"
                    />
                    <p className="text-xs text-gray-400 mt-1">Optional. Auto-generated if left empty.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Content</label>
                    <textarea
                      value={textDocContent}
                      onChange={(e) => setTextDocContent(e.target.value)}
                      placeholder="Paste your text content here — FAQ answers, product descriptions, policies, procedures, or any information your agent should know about..."
                      rows={12}
                      className="w-full px-3 py-2.5 bg-white border-2 border-[#1a1a2e] rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none transition-colors resize-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setShowUploadModal(false);
                      setTextDocName('');
                      setTextDocContent('');
                    }}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleAddText}
                    disabled={!textDocContent.trim() || isAddingText}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-emerald-500 rounded-xl hover:bg-emerald-600 border-2 border-[#1a1a2e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAddingText ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Add Text
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
